import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import { CompanyDto, companyValidationSchema } from 'dtos'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'

interface CompanyAddEditDialogProps {
  initialValues: CompanyDto
  loading: boolean
  onClose: () => void
  onSave: (values: CompanyDto) => void
  open: boolean
}

// R42 hard codes the Broker and Registered Importer companies.
// Maxwell Sommer 7/22/2024
export default function CompanyAddEditDialog({ initialValues, loading, onClose, onSave, open }: CompanyAddEditDialogProps) {
  // In VTS2, there can only ever be one broker company at a time. We get the broker (if it exists,) to determine if the isBrokerCompany switch is enabled/disabled.
  // const [brokerCompanyAlreadyExists, setBrokerCompanyAlreadyExists] = useState<boolean>(false)
  // const [isGettingBrokerCompany, setIsGettingBrokerCompany] = useState<boolean>(false)

  // In VTS2, there can only ever be one registered importer company at a time. We get the registered importer (if it exists,) to determine if the isRegisteredImporterCompany switch is enabled/disabled.
  // const [isGettingRegisteredImporter, setIsGettingRegisteredImporter] = useState<boolean>(false)
  // const [registeredImporterCompanyAlreadyExists, setRegisteredImporterCompanyAlreadyExists] = useState<boolean>(false)

  //
  const [buyerCompanies, setBuyerCompanies] = useState<CompanyDto[]>([])
  const [inboundTransporterCompanies, setInboundTransporterCompanies] = useState<CompanyDto[]>([])
  const [outboundTransporterCompanies, setOutboundTransporterCompanies] = useState<CompanyDto[]>([])
  const [titleDestinationCompanies, setTitleDestinationCompanies] = useState<CompanyDto[]>([])
  const [vehicleDestinationCompanies, setVehicleDestinationCompanies] = useState<CompanyDto[]>([])

  useEffect(() => {
    if (initialValues) {
      controllers.getCompanies({ page: 0, pageSize: 2000, order: 'asc', orderBy: 'name', statuses: ['isBuyerCompany'] }).then(response => {
        setBuyerCompanies(response.value.filter(company => company.companyGuid !== initialValues.companyGuid))
      })

      controllers.getCompanies({ page: 0, pageSize: 2000, order: 'asc', orderBy: 'name', statuses: ['isInboundTransporterCompany'] }).then(response => {
        setInboundTransporterCompanies(response.value.filter(company => company.companyGuid !== initialValues.companyGuid))
      })

      controllers.getCompanies({ page: 0, pageSize: 2000, order: 'asc', orderBy: 'name', statuses: ['isOutboundTransporterCompany'] }).then(response => {
        setOutboundTransporterCompanies(response.value.filter(company => company.companyGuid !== initialValues.companyGuid))
      })

      controllers.getCompanies({ page: 0, pageSize: 2000, order: 'asc', orderBy: 'name', statuses: ['isTitleDestinationCompany'] }).then(response => {
        setTitleDestinationCompanies(response.value.filter(company => company.companyGuid !== initialValues.companyGuid))
      })

      controllers.getCompanies({ page: 0, pageSize: 2000, order: 'asc', orderBy: 'name', statuses: ['isVehicleDestinationCompany'] }).then(response => {
        setVehicleDestinationCompanies(response.value.filter(company => company.companyGuid !== initialValues.companyGuid))
      })
    }
  }, [initialValues])

  // Get broker and registered importer.
  // useEffect(() => {
  //   if (open) {
  //     setIsGettingBrokerCompany(true)
  //     controllers
  //       .getBrokerCompany()
  //       .then(response => {
  //         if (response.value) {
  //           setBrokerCompanyAlreadyExists(response.value.companyGuid !== initialValues.companyGuid)
  //         } else {
  //           setBrokerCompanyAlreadyExists(false)
  //         }
  //       })
  //       .finally(() => {
  //         setIsGettingBrokerCompany(false)
  //       })

  //     setIsGettingRegisteredImporter(true)
  //     controllers
  //       .getRegisteredImporterCompany()
  //       .then(response => {
  //         if (response.value) {
  //           setRegisteredImporterCompanyAlreadyExists(response.value.companyGuid !== initialValues.companyGuid)
  //         } else {
  //           setRegisteredImporterCompanyAlreadyExists(false)
  //         }
  //       })
  //       .finally(() => {
  //         setIsGettingRegisteredImporter(false)
  //       })
  //   }
  // }, [initialValues.companyGuid, open])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur
        validateOnChange
        validationSchema={companyValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>{values.companyGuid ? 'Edit' : 'Add'} Company</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.name && errors.name)}
                      fullWidth
                      helperText={touched.name && errors.name}
                      label='Company Name'
                      name='name'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.website && errors.website)}
                      fullWidth
                      helperText={touched.website && errors.website}
                      label='Website'
                      name='website'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.website || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={loading || isSubmitting}
                      error={Boolean(touched.notes && errors.notes)}
                      fullWidth
                      helperText={touched.notes && errors.notes}
                      label='Notes'
                      maxRows={4}
                      minRows={4}
                      multiline
                      name='notes'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.notes || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isActive}
                          disabled={loading || isSubmitting}
                          name='isActive'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isActive}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Active</Typography>
                          <Typography variant='body2' color='#808080'>
                            Marks Active or Inactive
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isAttorneyCompany}
                          disabled={loading || isSubmitting}
                          name='isAttorneyCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isAttorneyCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Attorney Company</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Attorney company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isBrokerCompany}
                          // disabled={loading || isSubmitting || isGettingBrokerCompany || brokerCompanyAlreadyExists}
                          disabled
                          name='isBrokerCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isBrokerCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Broker</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Broker company and an option for "Broker Address" will appear on the Address Details dialog
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isBuyerCompany}
                          disabled={loading || isSubmitting}
                          name='isBuyerCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isBuyerCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Buyer Company</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Buyer company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isExporterCompany}
                          disabled={loading || isSubmitting}
                          name='isExporterCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isExporterCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Exporter Company</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Exporter company. Must select all associated companies for this company below for this Exporter.
                            Supports exporter company users with VTS2 Access.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isClusterConversionCompany}
                          disabled={loading || isSubmitting}
                          name='isClusterConversionCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isClusterConversionCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Cluster Conversion Company</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Cluster Conversion company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isClusterProgrammerCompany}
                          disabled={loading || isSubmitting}
                          name='isClusterProgrammerCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isClusterProgrammerCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Cluster Programmer Company</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Cluster Programmer company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isInboundTransporterCompany}
                          disabled={loading || isSubmitting}
                          name='isInboundTransporterCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isInboundTransporterCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Inbound Transporter</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Inbound Transporter company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isImporterDealerCompany}
                          disabled={loading || isSubmitting}
                          name='isImporterDealerCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isImporterDealerCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Importer Dealer Company</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Importer Dealer company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isInspectionCompany}
                          disabled={loading || isSubmitting}
                          name='isInspectionCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isInspectionCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Inspection Company</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Inspection company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isInsuranceCompany}
                          disabled={loading || isSubmitting}
                          name='isInsuranceCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isInsuranceCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Insurance Company</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Insurance company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isLienHolderCompany}
                          disabled={loading || isSubmitting}
                          name='isLienHolderCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isLienHolderCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Lien Holder Company (First Secured Interest)</Typography>
                          <Typography variant='body2' color='#808080'>
                            Lien Holder Company aka First Secured Interest is used for Title Applications.
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isMichiganSOSBranch}
                          disabled={loading || isSubmitting}
                          name='isMichiganSOSBranch'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isMichiganSOSBranch}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Michigan SoS Branch</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Michigan Secretary of State Branch
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isOutboundTransporterCompany}
                          disabled={loading || isSubmitting}
                          name='isOutboundTransporterCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isOutboundTransporterCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Outbound Transporter</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Outbound Transporter company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isRegisteredImporterCompany}
                          // disabled={
                          //   loading || isSubmitting || isGettingRegisteredImporter || registeredImporterCompanyAlreadyExists
                          // }
                          disabled
                          name='isRegisteredImporterCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isRegisteredImporterCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Registered Importer</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Registered Importer company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isSuretyCompany}
                          disabled={loading || isSubmitting}
                          name='isSuretyCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isSuretyCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Surety Company</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Surety company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isTitleDestinationCompany}
                          disabled={loading || isSubmitting}
                          name='isTitleDestinationCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isTitleDestinationCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Title Destination</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Title Destination company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isVehicleDestinationCompany}
                          disabled={loading || isSubmitting}
                          name='isVehicleDestinationCompany'
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.isVehicleDestinationCompany}
                        />
                      }
                      label={
                        <Grid>
                          <Typography>Vehicle Destination</Typography>
                          <Typography variant='body2' color='#808080'>
                            Indicates that the company is the Vehicle Destination company
                          </Typography>
                        </Grid>
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Company Name:**

A required free-text field

***Website:***

A free-text field

***Notes:***

A free-text field

***Active Switch:***

Marks the company as active or Inactive

***Attorney Company Switch:***

Indicates that the company is the Attorney Company

**Broker Company Switch:**
Indicates that the company is the Broker Company.

   - When enabled, an option for Broker Address will appear in the Address Details
dialog.

   - There can only be one active Broker Company within the system

***Buyer Company Switch:***

Indicates that the company is the Buyer Company

**Exporter Company Switch:**

Indicates that the company is the Exporter company.

Enabling this switch displays the following required drop-downs:

   - Buyer Companies

   - Inbound Transporter Companies

   - Outbound Transporter Companies

   - Title Destination Companies

   - Vehicle Destination Companies

**Buyer Companies Drop-Down:**
The drop down displays all active Buyer Companies within the system.

   - User can select multiple companies

   - This drop down is required

**Inbound Transporter Companies Drop-Down:**
The drop down displays all active Inbound Transporter Companies within the
system.

   - User can select multiple companies

   - This drop down is required

**Outbound Transporter Companies Drop-Down:**
The drop down displays all active Outbound Transporter Companies within the
system.

   - User can select multiple companies

   - This drop down is required

**Title Destination Companies Drop-Down:**
The drop down displays all active Title Destination Companies within the system.

   - User can select multiple companies

   - This drop down is required

**Vehicle Destination Companies Drop-Down:**
The drop down displays all active Vehicle Destination Companies within the
system.

   - User can select multiple companies

   - This drop down is required

**Cluster Conversion Company Switch:**

Indicates that the company is the Cluster Conversion Company.

**Cluster Programmer Company Switch:**

Indicates that the company is the Cluster Programmer Company.

**Inbound Transporter Company Switch:**

Indicates that the company is the Inbound Transporter Company.

**Importer Dealer Company Switch:**

Indicates that the company is the Importer Dealer Company.

**Inspection Company Switch:**

Indicates that the company is the Inspection Company.

**Insurance Company Switch:**

Indicates that the company is the Insurance Company.

**Lien Holder Company (First Secured Interest) Switch:**

Indicates that the company is the Lien Holder Company

   - Used in Title Applications to represent the first secured interest.                

**Register Importer Switch:**

Indicates that the company is the Registered Importer.

**Surety Company Switch:**

Indicates that the company is the Surety Company.

**Michigan SoS Branch Switch:**

Indicates that the company is the Michigan Secretary Of State Branch 

**Title Destination Company Switch:**

Indicates that the company is the Title Destination Company.

**Vehicle Destination Company Switch:**

Indicates that the company is the Vehicle Destination Company.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes.

**SAVE Button:**

Clicking the SAVE button will save any changes.`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={loading || isSubmitting} variant='text' color='inherit' onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton loading={loading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
